/**
 * Remembers which element opened the streak sheet so closing it can return
 * focus there. A module-level holder because the pill (header) and the sheet
 * (`AppShell`) sit in different React subtrees — same shape as
 * `videoPauseCoordinator`. Empty means the sheet auto-opened, so focus stays put.
 */
let triggerElement: HTMLElement | null = null;

/** ~1s at 60fps — outlasts the sheet's exit animation on a loaded device. */
const FOCUS_REASSERT_FRAMES = 60;

export const rememberStreakTrigger = (element: HTMLElement | null): void => {
  triggerElement = element;
};

/**
 * Re-asserted across frames, not set once: `BaseBottomSheet` blurs during its
 * exit animation and offers no "closed" callback, so a single `focus()` is
 * silently undone.
 *
 * It watches for the whole window rather than giving up the first time focus
 * looks taken. The close button holds focus for the frames between the tap and
 * the sheet unmounting, so bailing on "someone owns focus" meant never
 * restoring it on the commonest close path. A user who deliberately moves focus
 * to something that stays mounted still keeps it — we only ever claim focus
 * that nothing owns.
 */
export const restoreStreakTriggerFocus = (): void => {
  const element = triggerElement;
  triggerElement = null;
  if (!element) return;

  let framesLeft = FOCUS_REASSERT_FRAMES;

  const reassert = () => {
    framesLeft -= 1;
    const active = document.activeElement;
    // Unowned means: nothing focused, the body, or an element that has since
    // been unmounted — which is what the sheet's close button becomes.
    const isFocusUnowned =
      active === null || active === document.body || !active.isConnected;

    // The pill may have unmounted too (screen change, `canClaim` flip).
    if (element.isConnected && isFocusUnowned) element.focus();

    if (framesLeft > 0) requestAnimationFrame(reassert);
  };

  requestAnimationFrame(reassert);
};
